import React, { useEffect, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { Box, styled } from '@mui/material'
import { HorizontalTabs } from 'packages/eid-ui'
import { DropdownMenu } from 'components/DropdownMenu'
import { useRouteOptions, useCSVInboxTypes } from 'hooks'
import config from 'config'
import { SubPathLevel } from 'appConfiguration'
import { useTranslation } from 'react-i18next'
import EidButton from 'packages/eid-ui/EidButton'
import { useQuery } from 'packages/core'
import { useIsSmallScreenFalse } from 'packages/core'
import { usePageContext } from 'pageContext'

const SubHeaderTabs = styled(HorizontalTabs)(({ theme }) => ({
    '& .MuiTabs-flexContainer': {
        height: theme.mixins.navBar.minHeight,
        marginTop: '8px',
    },
    '& .MuiTabs-indicator': {
        backgroundColor: theme?.palette?.primary?.main,
        height: '4px',
        boxSizing: 'content-box',
        marginBottom: '0px',
        zIndex: 1,
    },
    '& .MuiTab-textColorInherit.Mui-selected': {
        fontWeight: 'bold',
        letterSpacing: '0.022em',
    },
    '& .MuiTab-root': {
        fontSize: '14px',
        letterSpacing: '0.3px',
        transition: 'color 0.15s ease-in-out',
        padding: '6px 8px',
        margin: '6px 8px',
        '&:hover': {
            color: theme.palette.primary.main,
        },
    },
}))

const StyledNavBarContainer = styled(Box)({
    height: '80px',
    marginBottom: '-4px',
    position: 'relative',
    whiteSpace: 'nowrap',

    '&::before': {
        content: '""',
        display: 'block',
        position: 'absolute',
        left: 0,
        top: '-4px',
        width: '100%',
        height: 'calc(100% - 2px)',
        borderBottom: '1px solid #d8d8dd',
    },

    '&::after': {
        content: '""',
        display: 'block',
        width: '200%',
        // transform: 'translateX(-50%)',
        height: '16px',
        backgroundImage:
            'linear-gradient(to top, rgba(238, 240, 244, 0), rgba(238, 240, 244, 0.9))',
        position: 'absolute',
        left: '-50%',
        bottom: '-10px',
    },
})

const SubHeader = ({
    showNavTabs = true,
    subHeaderButton,
    viewSwitcher,
    totalCount,
    showBackButton = false,
    backButtonLabel = 'Back',
}) => {
    const { routeOptions } = useRouteOptions()
    const { t } = useTranslation()
    const history = useHistory()
    const query = useQuery()
    const returnUrl = query.get('returnUrl')
    const {
        location: { pathname },
    } = history
    const isSmallScreen = useIsSmallScreenFalse()
    const pageContextValue = usePageContext()
    // usePageContext returns [state, dispatch] or {} if outside provider
    const dispatch = Array.isArray(pageContextValue)
        ? pageContextValue[1]
        : null
    const { data: inboxTypes = [] } = useCSVInboxTypes(true)

    // Track the last pathname to only sync when route actually changes
    const lastPathnameRef = useRef(null)

    const areaName = pathname.split('/')[1 + SubPathLevel]

    // Sync inboxType filter with route changes
    useEffect(() => {
        // Only proceed if we have dispatch (within PageContextProvider) and pathname changed
        if (!dispatch || lastPathnameRef.current === pathname) {
            return // No dispatch available or pathname hasn't changed, don't sync
        }

        if (!inboxTypes.length) return

        // Extract current route from pathname
        const pathParts = pathname.split('/')
        const routeName = pathParts[1 + SubPathLevel] // 'all' or 'GroupEligibility'

        // Update ref to current pathname
        lastPathnameRef.current = pathname

        // If route is exactly "/all" or routeName is undefined, select all inbox types
        if (routeName === 'all' || !routeName) {
            const allTypes = inboxTypes
                .filter((type) => type.active !== false)
                .map((type) => ({
                    id: type.csvInboxTypeID,
                    name: type.name,
                    friendlyName: type.friendlyName || type.name,
                    ...type,
                }))

            dispatch({
                type: 'SET_PROP',
                payload: {
                    key: 'inboxType',
                    value: allTypes,
                },
            })
        } else {
            // If specific route is selected (e.g., /GroupEligibility), find and select that type
            const selectedType = inboxTypes.find(
                (type) => type.name === routeName && type.active !== false,
            )

            if (selectedType) {
                const typeValue = {
                    id: selectedType.csvInboxTypeID,
                    name: selectedType.name,
                    friendlyName:
                        selectedType.friendlyName || selectedType.name,
                    ...selectedType,
                }

                dispatch({
                    type: 'SET_PROP',
                    payload: {
                        key: 'inboxType',
                        value: [typeValue],
                    },
                })
            }
        }
    }, [pathname, inboxTypes, dispatch])

    let selectedRoute
    let selectedParentRoute

    // Normalize pathname for comparison (remove trailing slash)
    const normalizedPathname = pathname.replace(/\/$/, '')

    // First check if pathname matches a parent route directly
    for (let i = 0; i < routeOptions.length; i++) {
        const parentRoutePath = (config.APP_SUBPATH + routeOptions[i].route)
            .replace(/\/$/, '')
            .toLowerCase()
        if (parentRoutePath === normalizedPathname.toLowerCase()) {
            // If it matches parent route, use the first child as selectedRoute
            selectedRoute = routeOptions[i].children[0] || routeOptions[i]
            selectedParentRoute = routeOptions[i]
            break
        }
    }

    // If no parent match, check children
    if (!selectedRoute) {
        for (let i = 0; i < routeOptions.length; i++) {
            const foundRoute = routeOptions[i].children.find((c) => {
                const routePath = (config.APP_SUBPATH + c.route)
                    .replace(/\/$/, '')
                    .toLowerCase()
                return routePath === normalizedPathname.toLowerCase()
            })
            if (foundRoute) {
                selectedRoute = foundRoute
                selectedParentRoute = routeOptions[i]
                break
            }
        }
    }
    // Extract selected tab from pathname
    // Path structure: /rta/all or /rta/GroupEligibility
    const pathParts = pathname.split('/').filter(Boolean)
    const areaIndex = SubPathLevel + 1
    const routeName = pathParts[areaIndex] // 'all' or 'GroupEligibility'
    const selectedTab = routeName === 'all' ? 'inbox' : routeName

    const getPreviousPathFromUrl = () => {
        return returnUrl || `${config.APP_SUBPATH}${selectedRoute.route}`
    }

    return (
        <Box
            height={isSmallScreen ? '128px' : '80px'}
            display="flex"
            flexDirection={isSmallScreen ? 'column' : 'row'}
            alignItems={isSmallScreen ? 'start' : 'center'}
            justifyContent="space-between"
            width="100%"
            component={isSmallScreen ? 'div' : StyledNavBarContainer}
        >
            <Box
                display="flex"
                paddingLeft={isSmallScreen ? '8px' : '0px'}
                justifyContent="space-between"
            >
                {/* Drop Down Menu Here */}
                <DropdownMenu
                    routeOptions={routeOptions}
                    selectedRoute={selectedRoute}
                    selectedParentRoute={selectedParentRoute}
                    totalCount={totalCount}
                />
            </Box>

            {subHeaderButton}

            {showNavTabs && selectedRoute && selectedRoute.children && (
                <SubHeaderTabs
                    variant="standard"
                    value={selectedTab}
                    onChange={(_, value) => {
                        if (value === 'inbox') {
                            history.push(`${config.APP_SUBPATH}/all`)
                        } else {
                            // Navigate to the direct route: /rta/GroupEligibility
                            history.push(`${config.APP_SUBPATH}/${value}`)
                        }
                    }}
                >
                    {selectedRoute.children.map((child, index) => {
                        // Extract tab value from route
                        // '/all' -> 'inbox', '/GroupEligibility' -> 'GroupEligibility'
                        const tabValue =
                            child.route === '/all' || child.route === '/all/'
                                ? 'inbox'
                                : child.route.replace(/^\//, '') || 'inbox'

                        return (
                            <HorizontalTabs.Tab
                                key={`${child.route}-${index}`}
                                value={tabValue}
                                label={t(
                                    child.titleKey || child.title || 'Untitled',
                                )}
                            />
                        )
                    })}
                </SubHeaderTabs>
            )}
            <Box minWidth="102px" display="flex" justifyContent="right">
                {showBackButton ? (
                    <EidButton.BackButton
                        previousRoute={getPreviousPathFromUrl()}
                        label={t(backButtonLabel)}
                    />
                ) : null}
            </Box>
        </Box>
    )
}

export default SubHeader
